import { Link } from "react-router-dom";
import { Truck, MapPin, Trophy, Clock, ArrowRight } from "lucide-react";
import { SEO } from "../components/SEO";
import { FadeUp } from "../components/motion";
import { useLang } from "../i18n/LanguageContext";

// Page dédiée « Livraison de trophées à Montréal » (demande du propriétaire,
// 2026-10-23) : l'atelier est à Léry — cette page dit clairement, en texte ET en
// langage machine (schéma Service, zone desservie = Montréal), que Montréal est
// livrée. Secteurs réels de l'île ; délais = la date « livrer avant le » choisie
// par le client (champ obligatoire de la soumission). Aucun délai chiffré
// inventé : on promet la date choisie, pas un nombre de jours.
const SECTEURS = [
  "Ville-Marie", "Le Plateau-Mont-Royal", "Rosemont–La Petite-Patrie", "Villeray",
  "Hochelaga-Maisonneuve", "Mercier", "Côte-des-Neiges", "Notre-Dame-de-Grâce",
  "Outremont", "Le Sud-Ouest", "Verdun", "LaSalle", "Lachine", "Saint-Laurent",
  "Ahuntsic-Cartierville", "Saint-Léonard", "Montréal-Nord", "Rivière-des-Prairies",
  "Anjou", "Westmount",
];

export default function LivraisonMontreal() {
  const { lang } = useLang();
  const en = lang === "en";

  const titre = en ? "Trophy delivery in Montréal" : "Livraison de trophées à Montréal";
  const intro = en
    ? "Every piece is produced at our Léry workshop (Montérégie) and delivered everywhere on the island of Montréal — trophies, medals, plaques and engraving, personalized with name, logo, date and message. Prefer to save? Free pickup at the workshop."
    : "Chaque pièce est produite à notre atelier de Léry (Montérégie) et livrée partout sur l'île de Montréal — trophées, médailles, plaques et gravure personnalisés : nom, logo, date et message. Pour économiser : ramassage sans frais à l'atelier.";

  const serviceLd = {
    "@context": "https://schema.org", "@type": "Service",
    name: titre,
    url: "https://mrtrophee.ca/livraison-montreal",
    inLanguage: en ? "en-CA" : "fr-CA",
    serviceType: en ? "Custom trophy, medal and plaque delivery" : "Livraison de trophées, médailles et plaques personnalisés",
    provider: { "@id": "https://mrtrophee.ca/#entreprise" },
    areaServed: { "@type": "City", name: "Montréal",
                  containedIn: { "@type": "AdministrativeArea", name: "Québec" } },
  };
  const faqLd = {
    "@context": "https://schema.org", "@type": "FAQPage",
    mainEntity: [
      { "@type": "Question",
        name: en ? "Do you deliver trophies to Montréal?" : "Livrez-vous des trophées à Montréal ?",
        acceptedAnswer: { "@type": "Answer", text: en
          ? "Yes — produced at our Léry workshop and delivered across the island of Montréal, or free pickup at the workshop."
          : "Oui — produits à notre atelier de Léry et livrés partout sur l'île de Montréal, ou ramassage sans frais à l'atelier." } },
      { "@type": "Question",
        name: en ? "How fast is delivery to Montréal?" : "Quels sont les délais de livraison à Montréal ?",
        acceptedAnswer: { "@type": "Answer", text: en
          ? "Tell us your “need-by” date in your quote request (it is a required field): we produce quickly in-house and commit to your date."
          : "Indiquez votre date « livrer avant le » dans votre demande de soumission (champ obligatoire) : la production est rapide, sur place, et nous nous engageons sur votre date." } },
    ],
  };

  return (
    <div className="pt-32 pb-28 max-w-4xl mx-auto px-6 lg:px-10" data-testid="livraison-montreal">
      <SEO title={`${titre} | Mr Trophée`}
        description={intro}
        path="/livraison-montreal"
        jsonLd={[serviceLd, faqLd]} />

      <FadeUp>
        <p className="text-gold tracking-[0.3em] uppercase text-xs mb-4" data-testid="lm-kicker">
          {en ? "Montréal delivery" : "Desserte de Montréal"}
        </p>
        <h1 className="font-display text-4xl sm:text-5xl text-ivory leading-tight mb-6">{titre}</h1>
        <p className="text-ivory/70 leading-relaxed mb-10" data-testid="lm-intro">{intro}</p>
      </FadeUp>

      <FadeUp delay={0.05}>
        <div className="grid sm:grid-cols-3 gap-4 mb-12" data-testid="lm-etapes">
          {[
            { Icon: Trophy, t: en ? "Produced in-house" : "Produit sur place",
              d: en ? "Made and personalized at our Léry workshop — laser engraving, colour printing."
                    : "Fabriqué et personnalisé à l'atelier de Léry — gravure laser, impression couleur." },
            { Icon: Clock, t: en ? "Your date, guaranteed" : "Votre date, respectée",
              d: en ? "The “need-by” date is required in every quote request — we commit to it."
                    : "La date « livrer avant le » est obligatoire dans chaque soumission — nous nous y engageons." },
            { Icon: Truck, t: en ? "Delivered or picked up" : "Livré ou ramassé",
              d: en ? "Delivery across Montréal, or free pickup at the workshop — your choice at checkout."
                    : "Livraison partout à Montréal, ou ramassage sans frais à l'atelier — votre choix à la caisse." },
          ].map((e, i) => (
            <div key={i} data-testid={`lm-etape-${i}`}
              className="bg-surface border border-ivory/10 rounded-xl p-6 hover:border-gold/40 transition-colors duration-300">
              <e.Icon className="w-6 h-6 text-gold mb-3" />
              <p className="text-sm font-bold text-ivory mb-1.5">{e.t}</p>
              <p className="text-xs text-ivory/60 leading-relaxed">{e.d}</p>
            </div>
          ))}
        </div>
      </FadeUp>

      <FadeUp delay={0.1}>
        <div className="mb-12" data-testid="lm-secteurs">
          <p className="text-[11px] uppercase tracking-widest text-gold/70 mb-3 flex items-center gap-2">
            <MapPin className="w-3.5 h-3.5" />
            {en ? "Neighbourhoods served across Montréal" : "Secteurs livrés partout à Montréal"}
          </p>
          <div className="flex flex-wrap gap-2">
            {SECTEURS.map((s) => (
              <span key={s} data-testid={`lm-secteur-${s.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                className="text-xs text-ivory/70 border border-ivory/15 rounded-full px-3 py-1.5">
                {s}
              </span>
            ))}
          </div>
          <p className="text-xs text-ivory/50 mt-3">
            {en ? "…and everywhere else on the island. Off-island? We ship across Canada."
                : "…et partout ailleurs sur l'île. Hors de l'île ? Nous livrons partout au Canada."}
          </p>
        </div>
      </FadeUp>

      <FadeUp delay={0.15}>
        <div className="flex flex-wrap gap-3">
          <Link to="/soumission-rapide" data-testid="lm-cta-soumission"
            className="inline-flex items-center gap-2 bg-gold text-ink px-7 py-3.5 rounded-full hover:bg-champagne transition-colors duration-300 text-sm font-bold">
            {en ? "Get a quote in 30 seconds" : "Soumission en 30 secondes"}
            <ArrowRight className="w-4 h-4" />
          </Link>
          <Link to="/villes/montreal" data-testid="lm-cta-ville"
            className="inline-flex items-center gap-2 border border-gold/40 text-gold px-7 py-3.5 rounded-full hover:bg-gold hover:text-ink transition-colors duration-300 text-sm">
            {en ? "Trophies and medals in Montréal" : "Trophées et médailles à Montréal"}
          </Link>
        </div>
      </FadeUp>
    </div>
  );
}
